/** Map the current device JSON onto the field names the UI already reads. */

const pick = (obj, keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const comIdsFromGroups = (groups) => {
  const ids = new Set();
  const lists = groups?.Com_State || groups?.com_state || [];
  if (!Array.isArray(lists)) return ids;
  lists.forEach((sub) => {
    if (!Array.isArray(sub)) return;
    sub.forEach((id) => {
      const n = Number(id);
      if (Number.isFinite(n)) ids.add(n);
    });
  });
  return ids;
};

const MAX_ARC_LEVEL = 5;

const protocolKey = (value) => {
  if (typeof value === "string") return value.trim().toLowerCase();
  if (value && typeof value === "object") {
    if (asNumber(value.lan_connectivity) === 1) return "tcpip";
    if (asNumber(value.wifi_connectivity) === 1) return "wifi";
  }
  return "";
};

const deviceProtocol = (device, streamProtocol) =>
  [
    device?.connection_state,
    device?.connection_type,
    device?.protocol,
    device?.device_info?.connection_state,
    device?.device_info?.connection_type,
    device?.device_info?.protocol,
  ]
    .map(protocolKey)
    .find(Boolean) || streamProtocol;

/**
 * WiFi H-LINKs report arc level 0–4 for the states TCP/IP reports as 1–5, so WiFi levels are
 * shifted onto the TCP/IP scale. The device value is kept in `arc_level_raw`.
 */
const scaleArcLevel = (sensor, sensorId, comState, wifi) => {
  if (sensor.arc_level_raw !== undefined) {
    return { arcLevel: asNumber(sensor.arc_level, 0), rawLevel: asNumber(sensor.arc_level_raw, 0) };
  }
  const rawLevel = asNumber(pick(sensor, ["arc_level", "Arc_Level"]), 0);
  const shift = wifi && comState === 1 && sensorId >= 1 && sensorId <= 12;
  return { arcLevel: shift ? Math.min(MAX_ARC_LEVEL, rawLevel + 1) : rawLevel, rawLevel };
};

const normalizeSensor = (sensor, comFromGroup, wifi) => {
  const sensorId = asNumber(pick(sensor, ["sensor_id", "id"]), 0);
  const ownCom = asNumber(pick(sensor, ["com_state"]), 0) === 1;
  const comState = comFromGroup ? (comFromGroup.has(sensorId) ? 1 : 0) : ownCom ? 1 : 0;
  const arcAlarm = asNumber(pick(sensor, ["arc_state", "Arc_Alarm", "arc_alarm"]), 0);
  const { arcLevel, rawLevel } = scaleArcLevel(sensor, sensorId, comState, wifi);
  const irTemp = asNumber(pick(sensor, ["ir_temperature", "IR_Temp", "ir_temp"]), 0);
  const setTemp = asNumber(
    pick(sensor, ["threshold_value", "ir_alarm_set_temp", "IR_Set_Temp"]),
    0
  );
  const arcThreshold = asNumber(pick(sensor, ["arc_threshold", "Arc_Threshold_Value"]), 0);
  const tempState = asNumber(pick(sensor, ["temp_state", "Temp_STATE", "Temp_Alarm"]), 0);
  const spotPoint = asNumber(pick(sensor, ["ir_spot_point", "IR_Spot_Point"]), 0);

  return {
    ...sensor,
    sensor_id: sensorId || sensor.sensor_id,
    com_state: comState,
    com_error: asNumber(pick(sensor, ["com_error"]), 0),
    rawdata_ready: asNumber(pick(sensor, ["rawdata_ready"]), 0),
    arc_state: arcAlarm,
    arc_level: arcLevel,
    arc_level_raw: rawLevel,
    arc_threshold: arcThreshold,
    ir_temperature: irTemp,
    threshold_value: setTemp,
    ir_alarm_set_temp: setTemp,
    temp_state: tempState,
    ir_spot_point: spotPoint,
  };
};

const normalizeDevice = (device, streamProtocol) => {
  if (!device || typeof device !== "object") return device;
  const wifi = deviceProtocol(device, streamProtocol) === "wifi";
  const sensors = Array.isArray(device.sensors) ? device.sensors : [];
  const comIds = comIdsFromGroups(device.groups);
  const anyDirectCom = sensors.some(
    (sensor) => asNumber(sensor?.sensor_id) <= 12 && asNumber(sensor?.com_state) === 1
  );
  // Wi-Fi payloads list live sensors in groups.Com_State and leave sensor.com_state at 0.
  const comFromGroup = !anyDirectCom && comIds.size ? comIds : null;
  return {
    ...device,
    sensors: sensors.map((sensor) => normalizeSensor(sensor, comFromGroup, wifi)),
  };
};

export const normalizeLivePayload = (payload) => {
  if (!payload || !Array.isArray(payload.devices)) return payload;
  const streamProtocol = protocolKey(payload.connection_state || payload.data?.connection_state);
  return {
    ...payload,
    devices: payload.devices.map((device) => normalizeDevice(device, streamProtocol)),
  };
};

const LIVE_PAYLOAD = Symbol("livePayload");

/**
 * Normalised device message with the parsed payload attached. `data` is only re-serialised
 * if a listener still reads it, so `readLivePayload` listeners parse each message once.
 */
const toLiveEvent = (event, payload) => {
  const live = new MessageEvent("message", { data: "", origin: event.origin, lastEventId: event.lastEventId });
  let text = null;
  Object.defineProperty(live, "data", {
    get() {
      if (text === null) text = JSON.stringify(payload);
      return text;
    },
  });
  Object.defineProperty(live, LIVE_PAYLOAD, { value: payload });
  return live;
};

/**
 * Returns a message normaliser for one socket. A frame identical to the previous one reuses the
 * previous payload object, so unchanged data keeps its identity and memoised views skip work.
 * Payloads are shared between listeners and must be treated as read-only.
 */
export const createSocketMessageNormalizer = () => {
  const byEvent = new WeakMap();
  let lastRaw = null;
  let lastPayload = null;
  return (event) => {
    if (!event || typeof event.data !== "string") return event;
    const cached = byEvent.get(event);
    if (cached) return cached;
    let result = event;
    try {
      if (event.data === lastRaw && lastPayload) {
        result = toLiveEvent(event, lastPayload);
      } else {
        const parsed = JSON.parse(event.data);
        if (parsed && Array.isArray(parsed.devices)) {
          lastRaw = event.data;
          lastPayload = normalizeLivePayload(parsed);
          result = toLiveEvent(event, lastPayload);
        }
      }
    } catch {
      result = event;
    }
    byEvent.set(event, result);
    return result;
  };
};

export const normalizeSocketMessage = createSocketMessageNormalizer();

/** Parsed payload of a socket message; normalised device messages are not parsed again. */
export const readLivePayload = (event) => event?.[LIVE_PAYLOAD] ?? JSON.parse(event.data);
